import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCheck, FiTrendingUp } = FiIcons;

const PublishingPathSelector = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});

  const questions = [
    {
      id: 'outcome',
      text: 'What is your primary publishing outcome?',
      weight: 3,
      options: [
        { text: 'Broad mainstream reach + credibility', value: 'trad', trad: 3, hybrid: 0, indie: 0 },
        { text: 'Credible partner + faster timeline', value: 'hybrid', trad: 0, hybrid: 3, indie: 0 },
        { text: 'Speed to market + full control', value: 'indie', trad: 0, hybrid: 0, indie: 3 }
      ]
    },
    {
      id: 'mission',
      text: 'What are your mission priorities? (Pick up to 2)',
      weight: 2,
      multiSelect: true,
      maxSelect: 2,
      options: [
        { text: 'Prestige/recognition', value: 'prestige', trad: 2, hybrid: 0, indie: 0 },
        { text: 'Professional polish with shared help', value: 'polish', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Agility/ownership/iteration', value: 'agility', trad: 0, hybrid: 0, indie: 2 }
      ]
    },
    {
      id: 'budget',
      text: 'What is your upfront budget?',
      weight: 3,
      options: [
        { text: '<$1,000', value: 'low', trad: 3, hybrid: 0, indie: 0 },
        { text: '$1,000–$6,000', value: 'medium', trad: 0, hybrid: 3, indie: 0 },
        { text: '$3,000–$12,000+', value: 'high', trad: 0, hybrid: 0, indie: 3 }
      ]
    },
    {
      id: 'revenue',
      text: 'What are your revenue goals?',
      weight: 2,
      options: [
        { text: 'OK with lower royalties for reach', value: 'low-royalty', trad: 2, hybrid: 0, indie: 0 },
        { text: 'Balanced royalties + support', value: 'balanced', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Max royalties, I\'ll invest', value: 'max-royalty', trad: 0, hybrid: 0, indie: 2 }
      ]
    },
    {
      id: 'timeline',
      text: 'What is your preferred timeline?',
      weight: 3,
      options: [
        { text: '12–24 months', value: 'long', trad: 3, hybrid: 0, indie: 0 },
        { text: '6–12 months', value: 'medium', trad: 0, hybrid: 3, indie: 0 },
        { text: '1–3 months', value: 'short', trad: 0, hybrid: 0, indie: 3 }
      ]
    },
    {
      id: 'bandwidth',
      text: 'What is your available bandwidth?',
      weight: 2,
      options: [
        { text: 'Low; need a partner', value: 'low', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Moderate; can manage some', value: 'moderate', trad: 0, hybrid: 1, indie: 1 },
        { text: 'High; can PM & learn', value: 'high', trad: 0, hybrid: 0, indie: 2 }
      ]
    },
    {
      id: 'control',
      text: 'How important is creative control?',
      weight: 3,
      options: [
        { text: 'OK with publisher control', value: 'publisher', trad: 3, hybrid: 0, indie: 0 },
        { text: 'Shared control with standards', value: 'shared', trad: 0, hybrid: 3, indie: 0 },
        { text: 'Full control non-negotiable', value: 'full', trad: 0, hybrid: 0, indie: 3 }
      ]
    },
    {
      id: 'nonnegotiables',
      text: 'What are your non-negotiables? (Select all that apply)',
      weight: 2,
      multiSelect: true,
      options: [
        { text: 'Theological/brand integrity priority', value: 'integrity', trad: 0, hybrid: 0, indie: 2 },
        { text: 'Must meet professional standards oversight', value: 'standards', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Willing to conform to house style', value: 'conform', trad: 2, hybrid: 0, indie: 0 }
      ]
    },
    {
      id: 'marketing',
      text: 'How do you feel about marketing?',
      weight: 3,
      options: [
        { text: 'Prefer publisher-driven', value: 'publisher', trad: 3, hybrid: 0, indie: 0 },
        { text: 'Lead with some support', value: 'supported', trad: 0, hybrid: 3, indie: 0 },
        { text: 'I\'ll own it fully', value: 'full', trad: 0, hybrid: 0, indie: 3 }
      ]
    },
    {
      id: 'platform',
      text: 'What is your current platform size?',
      weight: 2,
      options: [
        { text: 'Large/media-ready', value: 'large', trad: 2, hybrid: 0, indie: 0 },
        { text: 'Growing/consistent', value: 'growing', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Niche/start-up', value: 'niche', trad: 0, hybrid: 0, indie: 2 }
      ]
    },
    {
      id: 'credibility',
      text: 'How much do you need imprint validation?',
      weight: 2,
      options: [
        { text: 'High: need imprint validation', value: 'high', trad: 2, hybrid: 0, indie: 0 },
        { text: 'Moderate: credible partner helps', value: 'moderate', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Reader-first: results > imprint', value: 'reader-first', trad: 0, hybrid: 0, indie: 2 }
      ]
    },
    {
      id: 'risk',
      text: 'What is your risk tolerance?',
      weight: 2,
      options: [
        { text: 'Low: prefer guardrails', value: 'low', trad: 0, hybrid: 2, indie: 0 },
        { text: 'Moderate: willing to invest/learn', value: 'moderate', trad: 0, hybrid: 1, indie: 1 },
        { text: 'High: entrepreneurial', value: 'high', trad: 0, hybrid: 0, indie: 2 }
      ]
    }
  ];

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleResponse = (questionId, value, option) => {
    if (currentQ.multiSelect) {
      const current = responses[questionId] || [];
      const exists = current.find(item => item.value === value);
      
      if (exists) {
        // Remove if already selected
        setResponses(prev => ({
          ...prev,
          [questionId]: current.filter(item => item.value !== value)
        }));
      } else if (!currentQ.maxSelect || current.length < currentQ.maxSelect) {
        // Add if not at max
        setResponses(prev => ({
          ...prev,
          [questionId]: [...current, { value, option }]
        }));
      }
    } else {
      setResponses(prev => ({
        ...prev,
        [questionId]: { value, option }
      }));
    }
  };

  const calculateScores = () => {
    let scores = { trad: 0, hybrid: 0, indie: 0 };
    
    Object.entries(responses).forEach(([questionId, response]) => {
      if (Array.isArray(response)) {
        response.forEach(item => {
          scores.trad += item.option.trad || 0;
          scores.hybrid += item.option.hybrid || 0;
          scores.indie += item.option.indie || 0;
        });
      } else {
        scores.trad += response.option.trad || 0;
        scores.hybrid += response.option.hybrid || 0;
        scores.indie += response.option.indie || 0;
      }
    });
    
    return scores;
  };

  const determineRecommendation = (scores) => {
    const sorted = Object.entries(scores).sort(([,a], [,b]) => b - a);
    const top = sorted[0];
    const second = sorted[1];
    
    const confidence = Math.round(((top[1] - second[1]) / top[1]) * 100);
    let confidenceLevel = 'Close';
    if (confidence >= 30) confidenceLevel = 'High';
    else if (confidence >= 15) confidenceLevel = 'Moderate';
    
    return {
      path: top[0].charAt(0).toUpperCase() + top[0].slice(1),
      confidence: confidenceLevel
    };
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const scores = calculateScores();
      const recommendation = determineRecommendation(scores);
      onComplete(responses, scores, recommendation.path, recommendation.confidence);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const isCurrentQuestionAnswered = () => {
    const response = responses[currentQ.id];
    if (currentQ.multiSelect) {
      return response && response.length > 0;
    }
    return response;
  };

  const getSelectedCount = () => {
    const response = responses[currentQ.id];
    return response ? response.length : 0;
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-700">
              Question {currentQuestion + 1} of {questions.length}
            </h2>
            <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <div className="flex items-start gap-3 mb-6">
            <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiTrendingUp} className="text-xl text-white" />
            </div>
            <div className="flex-grow">
              <h3 className="text-2xl font-serif text-gray-900 leading-relaxed mb-2">
                {currentQ.text}
              </h3>
              {currentQ.multiSelect && (
                <p className="text-sm text-gray-600">
                  {currentQ.maxSelect ? `Select up to ${currentQ.maxSelect} options` : 'Select all that apply'}
                  {currentQ.maxSelect && ` (${getSelectedCount()}/${currentQ.maxSelect} selected)`}
                </p>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQ.options.map((option, index) => {
              const isSelected = currentQ.multiSelect
                ? responses[currentQ.id]?.some(item => item.value === option.value)
                : responses[currentQ.id]?.value === option.value;

              const isDisabled = currentQ.multiSelect && currentQ.maxSelect && 
                              getSelectedCount() >= currentQ.maxSelect && !isSelected;

              return (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: isDisabled ? 1 : 1.02 }}
                  whileTap={{ scale: isDisabled ? 1 : 0.98 }}
                  onClick={() => !isDisabled && handleResponse(currentQ.id, option.value, option)}
                  disabled={isDisabled}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    isSelected
                      ? 'border-accent bg-accent/10 shadow-lg'
                      : isDisabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : 'border-gray-200 hover:border-accent/50 hover:bg-accent/5'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${isSelected ? 'text-accent' : 'text-gray-700'}`}>
                      {option.text}
                    </span>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-6 h-6 bg-accent text-white rounded-full flex items-center justify-center"
                      >
                        <SafeIcon icon={FiCheck} className="text-sm" />
                      </motion.div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
              currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-accent hover:bg-accent/5'
            }`}
          >
            Previous
          </button>
          
          <button
            onClick={handleNext}
            disabled={!isCurrentQuestionAnswered()}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
              isCurrentQuestionAnswered()
                ? 'bg-accent hover:bg-orange-600 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {currentQuestion === questions.length - 1 ? 'Get Recommendation' : 'Next Question'}
            <SafeIcon icon={FiArrowRight} className="text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishingPathSelector;